import React, { useEffect, useMemo } from 'react';
import get from 'lodash/fp/get';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import createV2RequestSchema from '@careerstart/wae-common/schema/positionTemplates/create/v2/post.req';
import Box from '@mui/material/Box';

import {
  selectCreatedPositionTemplateId,
  selectIsProcessingPostPT,
} from '../../../main-deprecated/store/selectors/createPositionTemplateSelectors';
import { topBarHeight } from '../../../main-deprecated/theme';
import {
  clearCreatedPositionTemplate,
  postPositionTemplateV2,
} from '../../../main-deprecated/views/positionTemplate/createPositionTemplateDrawer/reducer';
import { validateSchema } from '../../components/Form/validations';
import { BLACK } from '../../theme/colorConstants';
import { showSnackbar } from '../app';
import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';

import BeforeYouSaveCard from './components/BeforeYouSaveCard';
import CreatePageHeader from './components/CreatePageHeader';
import SectionsNavCard from './components/SectionsNavCard';
import { getPunchDistanceError } from './helpers/additionalInfoValidation';
import buildCreatePositionTemplateV2Payload from './helpers/buildCreatePositionTemplateV2Payload';
import { getCreateReadiness, getSectionStatuses } from './helpers/createReadiness';
import usePositionTemplateForm from './hooks/usePositionTemplateForm';
import AdditionalInfoSection from './sections/AdditionalInfoSection';
import LegalInfoSection from './sections/LegalInfoSection';
import LocationsSection from './sections/LocationsSection';
import PayRatesSection from './sections/PayRatesSection';
import PositionDetailsSection from './sections/PositionDetailsSection';
import RequirementsSection from './sections/RequirementsSection';
import ShiftsSection from './sections/ShiftsSection';

const POSITION_TEMPLATES_PATH = `/${ROUTE_CONSTANTS.POSITION_TEMPLATES}`;

const PositionTemplateCreateViewV2 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isProcessing = useSelector(selectIsProcessingPostPT);
  const createdPositionTemplateId = useSelector(selectCreatedPositionTemplateId);
  const { values, setField } = usePositionTemplateForm();

  const readiness = useMemo(() => getCreateReadiness(values), [values]);
  const sectionStatuses = useMemo(() => getSectionStatuses(values), [values]);
  const isSubmitDisabled =
    !readiness.canCreate || !!getPunchDistanceError(values.additionalInfo) || isProcessing;

  useEffect(() => {
    if (!createdPositionTemplateId) return;
    dispatch(clearCreatedPositionTemplate());
    navigate(POSITION_TEMPLATES_PATH);
  }, [createdPositionTemplateId, dispatch, navigate]);

  const submit = () => {
    if (isSubmitDisabled) return;
    const payload = buildCreatePositionTemplateV2Payload(values);
    if (get('FORM_ERROR', validateSchema(payload, createV2RequestSchema))) {
      dispatch(showSnackbar({ message: 'error.positionTemplate.invalid' }));
      return;
    }
    dispatch(postPositionTemplateV2(payload));
  };

  return (
    <Box
      data-testid="position-template-create-view-v2"
      sx={{
        backgroundColor: BLACK[10],
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${topBarHeight})`,
        width: '100%',
      }}
    >
      <CreatePageHeader
        isCreateDisabled={isSubmitDisabled}
        onBack={() => navigate(POSITION_TEMPLATES_PATH)}
        onCreate={submit}
      />
      <Box
        sx={{
          alignItems: 'flex-start',
          display: 'flex',
          flex: 1,
          gap: '16px',
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 24px',
        }}
      >
        <Box
          data-testid="position-template-create-left"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: '16px',
            position: 'sticky',
            top: 0,
            width: '320px',
          }}
        >
          <BeforeYouSaveCard
            approveChecks={readiness.approveChecks}
            canApprove={readiness.canApprove}
            canCreate={readiness.canCreate}
            createChecks={readiness.createChecks}
          />
          <SectionsNavCard sections={sectionStatuses} />
        </Box>
        <Box
          data-testid="position-template-create-right"
          sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: '16px', minWidth: 0 }}
        >
          <PositionDetailsSection values={values} setField={setField} />
          <LocationsSection values={values} setField={setField} />
          <PayRatesSection values={values} setField={setField} />
          <ShiftsSection values={values} setField={setField} />
          <RequirementsSection values={values} setField={setField} />
          <AdditionalInfoSection values={values} setField={setField} />
          <LegalInfoSection values={values} setField={setField} />
        </Box>
      </Box>
    </Box>
  );
};

export default PositionTemplateCreateViewV2;
